import { AiLoader } from '@/components/shared/AiLoader';
import { CampaignSelector } from '@/components/shared/CampaignSelector';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ContentCalendar } from '@/components/shared/ContentCalendar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import GradualSpacing from '@/components/ui/gradual-spacing';
import { useCampaignContext } from '@/contexts/CampaignContext';
import {
  CampaignStatus,
  useDeleteCampaign,
  useDownloadCampaign,
} from '@/hooks/useCampaigns';
import { MoreVertical } from 'lucide-react';
import { ReactElement, useState } from 'react';
import { toast } from 'sonner';

export const ContentCalendarPage = (): ReactElement => {
  const { activeCampaign, isLoading, setActiveCampaign } = useCampaignContext();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { refetch: downloadCampaign, isFetching: isDownloading } =
    useDownloadCampaign(activeCampaign?.id, activeCampaign?.name);
  const { mutateAsync: deleteCampaign, isPending: isDeleting } =
    useDeleteCampaign();

  const showGeneratingLoader =
    activeCampaign && activeCampaign?.status !== CampaignStatus.COMPLETED;

  const overlayLoader = {
    isLoading:
      isLoading || activeCampaign?.status === CampaignStatus.GENERATING,
    customLoader: showGeneratingLoader ? <AiLoader /> : undefined,
    message: showGeneratingLoader ? undefined : 'Loading calendar...',
  };

  const downloadAsCSV = async () => {
    try {
      await downloadCampaign();
      toast.success('Campaign successfully downloaded as csv');
    } catch (error) {
      console.error('Failed to download csv', error);
      toast.error('Failed to download csv. Please try again.');
    }
  };

  const handleDeleteCampaign = async () => {
    try {
      if (!activeCampaign?.id) return;
      await deleteCampaign(activeCampaign.id);
      setActiveCampaign('all');
      toast.success('Campaign successfully deleted');
    } catch (error) {
      console.error('Failed to delete campaign', error);
      toast.error('Failed to delete campaign. Please try again.');
    }
  };

  return (
    <>
      <div className="flex md:flex-row justify-between items-end">
        <GradualSpacing
          className="font-display text-center text-3xl font-bold -tracking-widest text-black dark:text-white md:text-5xl md:leading-[4rem]"
          text="Content Calendar"
        />
        <div className="flex items-center gap-4">
          <CampaignSelector />
          {!showGeneratingLoader && (
            <>
              <Button onClick={downloadAsCSV} isLoading={isDownloading}>
                Download as CSV
              </Button>
              {activeCampaign && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive cursor-pointer"
                      onClick={() => setShowDeleteDialog(true)}>
                      Delete Campaign
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}

          <ConfirmDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            title="Delete Campaign"
            description="Are you sure you want to delete this campaign? This action cannot be undone."
            confirmText="Delete"
            onConfirm={handleDeleteCampaign}
            isConfirming={isDeleting}
            variant="destructive"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <ContentCalendar overlayLoader={overlayLoader} />
      </div>
    </>
  );
};
